import type { ServicePageData } from './types';

const signboards: ServicePageData = {
  slug: 'signboards',
  eyebrow: 'Signage',
  title: 'Communicate your message with',
  titleAccent: 'custom sign boards.',
  intro:
    'Our sign boards come in a range of materials, shapes and sizes. Chat to our design team, who will customise your sign to promote your business in the most eye-catching way.',
  heroImages: [
    '/home/signboards/signboards-mayfield.jpg',
    '/home/signboards/signboards-kearns-and-rouse.jpg',
    '/home/signboards/signboards-ijr.jpg',
    '/home/signboards/signboards-kemp.jpg',
    '/home/signboards/signboards-loadstreet.jpg',
    '/home/signboards/signboards-blackheart.jpg',
    '/home/signboards/signboards-banners.jpg',
    '/home/signboards/signboards-walkers.jpg',
    '/home/signboards/signboards-outpost.jpg',
    '/home/signboards/sign-tray.jpg',
    '/home/signboards/acrylic-panel.jpg',
    '/home/signboards/signboard-installation.jpg',
    '/home/signboards/signboards-mayfield.jpg',
    '/home/signboards/signboards-ijr.jpg',
    '/home/signboards/signboards-blackheart.jpg',
    '/home/signboards/signboards-kearns-and-rouse.jpg',
    '/home/signboards/signboards-walkers.jpg',
    '/home/signboards/signboards-loadstreet.jpg',
    '/home/signboards/signboards-kemp.jpg',
    '/home/signboards/signboards-banners.jpg',
    '/home/signboards/signboards-outpost.jpg',
  ],

  useCases: [
    { title: 'Shopfronts and fascias', text: 'Long-term exterior signage that survives the weather.' },
    { title: 'Reception and interior', text: 'Acrylic panels on stand-offs for a polished welcome.' },
    { title: 'Site and hoarding boards', text: 'Large-format composite panels, single or tiled.' },
    { title: 'Wayfinding', text: 'Directional and door signs in matching materials.' },
    { title: 'Events and temporary', text: 'Lightweight Correx and Foamex for short runs.' },
    { title: 'Estate agents and trades', text: 'Boards that go up fast and come down clean.' },
  ],

  optionsHeading: 'Choose your panel',
  optionsIntro: 'Sign boards are available in a range of materials to suit your requirements.',
  options: [
    {
      id: 'aluminium-composite',
      title: 'Aluminium composite',
      lead: 'Two sheets of coated aluminium with a solid polyethylene core.',
      body: 'Hardwearing, weather and rust resistant, making it the best solution for long-term external signage. Supplied in a standard white matt finish that can be vinyl wrapped or customised with decals.',
      image: '/home/signboards/aluminium-composite-panel.jpg',
      specs: [
        { label: 'Size', value: 'Up to 8 × 4 ft (2440 × 1220 mm). Larger boards are tiled from multiple panels.' },
        { label: 'Thickness', value: '3 mm with a 0.15 mm aluminium skin.' },
        { label: 'Finishes', value: 'Gloss, matt, anti-graffiti or dry-wipe lamination over print.' },
        { label: 'Fixings', value: 'Drilled and screwed. Matching screw caps supplied with installation. Pre-drilled on request.' },
      ],
    },
    {
      id: 'acrylic',
      title: 'Perspex acrylic',
      lead: 'Luxury interior or exterior signage that reads as high end.',
      body: 'Typically used at a business entrance to make the place feel professional and approachable. Designed to last for years, with polished edges shown off on metal stand-offs.',
      image: '/home/signboards/acrylic-panel.jpg',
      specs: [
        { label: 'Colours', value: 'Clear or 35 opaque colours, plus pastels in matt or mid-gloss. Mirror silver and gold for interiors.' },
        { label: 'Thickness', value: '3, 5, 8 or 10 mm. Square or rounded corners.' },
        { label: 'Stock sizes', value: '5 mm clear rounds, squares, rectangles and ovals from 100 mm to 420 × 297 mm.' },
        { label: 'Fixings', value: 'Stand-offs in three sizes, chrome or satin. Supplied pre-drilled.' },
      ],
    },
    {
      id: 'sign-tray',
      title: 'Sign tray',
      lead: 'A folded pan tray in 3 mm aluminium composite.',
      body: 'Robust, long-lasting exterior signage with returns that hide the fixings and give a fascia real depth. Available in a range of colours and finishes.',
      image: '/home/signboards/sign-tray.jpg',
      specs: [
        { label: 'Size', value: 'Limited by sheet size including returns. Larger fascias are made from multiple trays.' },
        { label: 'Colours', value: 'Standard composite colours, or wrapped in vinyl to match your brand.' },
        { label: 'Fixings', value: 'Fixed through the returns to a batten frame. Installation recommended.' },
      ],
    },
    {
      id: 'foamex',
      title: 'Foamex',
      lead: 'Lightweight rigid PVC for interior and short-term exterior use.',
      body: 'Smooth, easy to print or apply vinyl to, and light enough to hang or fix with tape or screws. A good choice for wayfinding, point of sale and exhibition panels.',
      image: '/home/signboards/foamex-panel.jpg',
      specs: [
        { label: 'Size', value: 'Up to 8 × 4 ft (2440 × 1220 mm).' },
        { label: 'Finishes', value: 'White matt, printed or vinyl applied. Laminated on request.' },
        { label: 'Fixings', value: 'Screws, double-sided tape or stand-offs.' },
      ],
    },
    {
      id: 'correx',
      title: 'Correx',
      lead: 'Fluted polypropylene, the estate-agent board material.',
      body: 'The cheapest way to get a message up quickly. Waterproof and light, ideal for temporary site boards, event signage and anything that needs cable-tying to a fence.',
      image: '/home/signboards/correx-panel.jpg',
      specs: [
        { label: 'Size', value: 'Up to 8 × 4 ft. Stock sizes available for estate-agent style boards.' },
        { label: 'Colours', value: 'White as standard, other stock colours available.' },
        { label: 'Fixings', value: 'Cable ties, screws or tape.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Printed vinyl',
      text: 'Full-colour graphics printed on our in-house wide-format printer, then laminated and applied to the panel. Photos, gradients and small detail.',
    },
    {
      title: 'Cut vinyl',
      text: 'A machine follows the design with a sharp blade, cutting shapes and letters from pre-coloured rolls. Sharper edges than print, with no background.',
    },
  ],

  gallery: [
    '/home/signboards/signboards-mayfield.jpg',
    '/home/signboards/signboards-kearns-and-rouse.jpg',
    '/home/signboards/signboards-ijr.jpg',
    '/home/signboards/signboards-kemp.jpg',
    '/home/signboards/signboards-loadstreet.jpg',
    '/home/signboards/signboards-blackheart.jpg',
    '/home/signboards/signboards-walkers.jpg',
    '/home/signboards/signboards-banners.jpg',
    '/home/signboards/signboards-outpost.jpg',
  ],

  info: [
    {
      title: 'Professional installation',
      text: 'Our installation team operates throughout the Midlands. We handle everything from site survey to final fitting.',
      bullets: ['Fully insured', 'Screw caps colour-matched to the board', 'Batten frames for trays and large panels'],
    },
    {
      title: 'Supply only',
      text: 'Self-install kits with pre-drilled holes and all necessary hardware. Fitting instructions included.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Turnaround',
      text: 'Typically 5 to 10 working days from approval depending on complexity. Need it sooner? Talk to us and we will do our best.',
    },
  ],

  cta: {
    title: 'Get your free sign board quote.',
    text: 'Tell us about your project and we will recommend the right panel, finish and fixing.',
    image: '/home/signboards/signboard-installation.jpg',
  },
};

export default signboards;
